import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import CliAuthShell from "@/components/cliAuth/CliAuthShell";
import CliAuthStatusCard from "@/components/cliAuth/CliAuthStatusCard";
import { getEnv } from "@/utils/env";
import {
  MAKO_CLI_CALLBACK_URL_KEY,
  MAKO_CLI_FLOW_KEY,
  MAKO_CLI_NONCE_KEY,
  validateCliCallbackUrl,
  validateCliNonce,
} from "@/utils/cliAuth";

const PROVIDER = "azure";
const SCOPES = ["openid", "offline_access"];

async function startCliOauth({ apiBaseUrl, provider }: { apiBaseUrl: string; provider: string }) {
  const url = new URL(`${apiBaseUrl}/auth/cli/${provider}/authorize`);
  for (const scope of SCOPES) {
    url.searchParams.append("scopes", scope);
  }

  const resp = await fetch(url.toString(), { credentials: "include" });
  if (!resp.ok) {
    throw new Error(`Authorize failed (${resp.status})`);
  }
  const body = await resp.json();
  if (!body?.authorization_url) {
    throw new Error("Authorize did not return authorization_url");
  }
  window.location.assign(body.authorization_url);
}

const searchSchema = z.object({
  callback_url: z.string().optional(),
  nonce: z.string().optional(),
  mode: z.string().optional(),
});

export const Route = createFileRoute("/auth/cli")({
  component: CliAuthPage,
  validateSearch: searchSchema,
  staticData: { noLayout: true },
});

function CliAuthPage() {
  const searchParams = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const callbackUrlParam = searchParams.callback_url;
    const nonceParam = searchParams.nonce;
    const modeParam = searchParams.mode;
    const callbackUrl = Array.isArray(callbackUrlParam)
      ? callbackUrlParam[0]
      : callbackUrlParam;
    const nonce = Array.isArray(nonceParam) ? nonceParam[0] : nonceParam;
    const mode = Array.isArray(modeParam) ? modeParam[0] : modeParam;
    const isManual = mode === "manual";

    const nonceValidation = validateCliNonce(nonce);
    if (!nonceValidation.ok) {
      setError((nonceValidation as { error: string }).error);
      return;
    }

    if (!isManual) {
      const callbackValidation = validateCliCallbackUrl(callbackUrl);
      if (!callbackValidation.ok) {
        setError((callbackValidation as { error: string }).error);
        return;
      }
      window.sessionStorage.setItem(
        MAKO_CLI_CALLBACK_URL_KEY,
        callbackValidation.normalized
      );
    } else {
      window.sessionStorage.removeItem(MAKO_CLI_CALLBACK_URL_KEY);
    }

    const apiBaseUrl = getEnv().NEXT_PUBLIC_CLIENT_SIDE_API_URL;
    if (!apiBaseUrl) {
      setError("Missing NEXT_PUBLIC_CLIENT_SIDE_API_URL.");
      return;
    }

    window.sessionStorage.setItem(MAKO_CLI_FLOW_KEY, isManual ? "manual" : "loopback");
    window.sessionStorage.setItem(MAKO_CLI_NONCE_KEY, nonce);

    startCliOauth({ apiBaseUrl, provider: PROVIDER }).catch((e) => {
      console.error(e);
      setError("Unable to start OAuth login.");
    });
  }, [searchParams]);

  const isError = Boolean(error);
  const statusTitle = isError ? "Unable to start login" : "Starting sign-in";
  const statusMessage = isError
    ? error!
    : "Opening your browser to complete login…";
  const statusTone = isError ? "error" : "info";
  const helperText = isError
    ? "Return to the CLI and try again."
    : "This window will update when the login finishes.";

  return (
    <CliAuthShell>
      <CliAuthStatusCard
        tone={statusTone}
        title={statusTitle}
        message={statusMessage}
      />
      <p className="mt-4 text-center text-xs text-grey-200">{helperText}</p>
    </CliAuthShell>
  );
}
